import { useCallback, useMemo, useState, type FormEvent } from 'react';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MoneyValue } from '@/components/ui/MoneyValue';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { cn } from '@/lib/utils';
import type { DeclaredExternalAccount, SemanticClassWire, StreamCandidate, StreamTransaction } from '@/types/onboarding';
import {
  CLASS_LABEL,
  DEFER_LABEL,
  resultNote,
  selectionHint,
  selectionStats,
  shortDate,
  streamCopy,
  whyCopy,
  withExplicitPlus,
  type StreamAnswer,
} from './streams.helpers';

export interface StreamCardProps {
  stream: StreamCandidate;
  index: number;
  count: number;
  answer: StreamAnswer | undefined;
  excludedIds: string[];
  txOpen: boolean;
  whyOpen: boolean;
  followUpOpen: boolean;
  posting: boolean;
  declaredAccounts: DeclaredExternalAccount[];
  onToggleTxList: (streamKey: string) => void;
  onToggleWhy: (streamKey: string) => void;
  onToggleTransaction: (streamKey: string, transactionId: string) => void;
  onSelectAll: (streamKey: string) => void;
  onClearSelection: (streamKey: string) => void;
  /** An option pick. Income on a self-paid stream opens the perimeter follow-up
   * instead of posting — the parent owns that routing. */
  onPick: (stream: StreamCandidate, cls: SemanticClassWire) => void;
  onDefer: (stream: StreamCandidate) => void;
  /** The follow-up's outcome: a declared external account id, a new account name
   * to declare first, or neither ("vem de outra pessoa"). */
  onConfirmIncome: (stream: StreamCandidate, source: { declaredExternalAccountId?: string; newAccountName?: string }) => void;
  onCancelFollowUp: () => void;
}

export function StreamCard(props: StreamCardProps) {
  const { stream, index, count, answer, excludedIds, posting } = props;
  const formatCurrency = useFormatCurrency();

  const stats = useMemo(
    () => selectionStats(stream.transactions, excludedIds),
    [stream.transactions, excludedIds],
  );
  const copy = streamCopy(stream.suggestedClass);
  const noneSelected = stats.selected.length === 0;
  const deferred = answer?.kind === 'deferred';

  const handleToggleTxList = useCallback(
    () => props.onToggleTxList(stream.streamKey),
    [props, stream.streamKey],
  );
  const handleToggleWhy = useCallback(
    () => props.onToggleWhy(stream.streamKey),
    [props, stream.streamKey],
  );
  const handleSelectAll = useCallback(
    () => props.onSelectAll(stream.streamKey),
    [props, stream.streamKey],
  );
  const handleClearSelection = useCallback(
    () => props.onClearSelection(stream.streamKey),
    [props, stream.streamKey],
  );
  const handleDefer = useCallback(() => props.onDefer(stream), [props, stream]);

  const monthlyText = noneSelected
    ? '—'
    : withExplicitPlus(formatCurrency(stats.monthly), stats.monthly);

  return (
    // min-w-0: the card mounts as a grid/flex child — without it the track sizes
    // to the card's content and the whole page overflows on narrow viewports.
    <Card className="min-w-0 p-5">
      <p className="font-mono text-2xs tracking-[0.14em] text-text-tertiary uppercase">
        Grupo {index + 1} de {count}
      </p>

      <div className="mt-2 flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-[15px]/snug font-bold text-pretty">{copy.question}</p>
          <p className="mt-1 font-mono text-[11px] break-words text-text-secondary">{stream.displayLabel}</p>
          <p className="mt-0.5 text-[11px] text-text-tertiary">
            {stream.accountName}
            {stream.cadenceHint === 'monthly' && ' · todo mês'}
          </p>
        </div>
        <p
          className={cn(
            'shrink-0 text-right font-mono text-[17px] font-bold tabular-nums',
            noneSelected
              ? 'text-text-secondary'
              : stats.monthly > 0
                ? 'text-amount-positive'
                : 'text-amount-negative',
          )}
        >
          <MoneyValue text={monthlyText} />
          <span className="mt-0.5 block font-sans text-[10px] font-medium text-text-tertiary">
            por mês
          </span>
        </p>
      </div>

      <div className="mt-3.5 flex items-center gap-3 border-t border-divider pt-3">
        <p className="min-w-0 flex-1 text-[11px] text-text-tertiary">
          {stats.selected.length} de {stats.total} lançamentos selecionados
        </p>
        <Button variant="link" size="sm" onClick={handleToggleTxList}>
          {props.txOpen ? 'ocultar lançamentos' : 'escolher lançamentos'}
        </Button>
      </div>

      {props.txOpen && (
        <div>
          <ul className="mt-1">
            {stream.transactions.map((tx) => (
              <StreamTransactionRow
                key={tx.id}
                streamKey={stream.streamKey}
                tx={tx}
                checked={!excludedIds.includes(tx.id)}
                frozen={Boolean(answer)}
                onToggle={props.onToggleTransaction}
              />
            ))}
          </ul>
          {/* The selection is frozen once answered — the result note describes the
              ids the confirmation posted, and no second confirmation is sent. */}
          {!answer && (
            <div className="mt-1.5 flex items-center gap-3">
              <Button variant="link" size="sm" onClick={handleSelectAll}>
                Selecionar todos
              </Button>
              <Button variant="link" size="sm" onClick={handleClearSelection}>
                Limpar seleção
              </Button>
            </div>
          )}
        </div>
      )}

      {answer ? (
        <div className="mt-3.5 flex flex-wrap items-center gap-2.5 border-t border-divider pt-3.5">
          <Badge variant={deferred ? 'default' : 'primary'}>
            {deferred ? DEFER_LABEL : CLASS_LABEL[(answer as Extract<StreamAnswer, { kind: 'classified' }>).class]}
          </Badge>
          <span className="min-w-0 flex-1 text-[11px] text-text-tertiary">
            {resultNote(stats, deferred)}
            {!deferred && ' Dá para ajustar depois no app.'}
          </span>
        </div>
      ) : (
        <div className="mt-3.5 border-t border-divider pt-3.5">
          <div className="flex flex-wrap gap-2">
            {copy.options.map((option) => (
              <StreamOptionButton
                key={option.class}
                stream={stream}
                cls={option.class}
                label={option.label}
                disabled={noneSelected || posting}
                onPick={props.onPick}
              />
            ))}
            {posting && <CircleNotchIcon size={16} className="animate-spin self-center text-text-tertiary" />}
          </div>
          <p className="mt-2 text-[11px] text-text-tertiary">{selectionHint(stats)}</p>

          {props.followUpOpen && (
            <IncomeFollowUp
              stream={stream}
              declaredAccounts={props.declaredAccounts}
              posting={posting}
              onConfirmIncome={props.onConfirmIncome}
              onCancel={props.onCancelFollowUp}
            />
          )}

          <div className="mt-2.5 flex items-center gap-3.5">
            <Button variant="link" size="sm" disabled={posting} onClick={handleDefer}>
              Não tenho certeza
            </Button>
            <Button variant="link" size="sm" onClick={handleToggleWhy}>
              {props.whyOpen ? 'ocultar o motivo' : 'por que perguntamos?'}
            </Button>
          </div>
          {props.whyOpen && (
            <p className="mt-2.5 rounded-xl bg-surface-sunken px-3 py-2.5 text-xs/5 text-pretty text-text-secondary">
              {whyCopy(stream.why)}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

function StreamTransactionRow({
  streamKey,
  tx,
  checked,
  frozen,
  onToggle,
}: {
  streamKey: string;
  tx: StreamTransaction;
  checked: boolean;
  frozen: boolean;
  onToggle: (streamKey: string, transactionId: string) => void;
}) {
  const formatCurrency = useFormatCurrency();
  const handleToggle = useCallback(() => onToggle(streamKey, tx.id), [onToggle, streamKey, tx.id]);

  return (
    <li className="flex items-center gap-2.5 border-t border-divider py-1.5">
      <Checkbox
        checked={checked}
        disabled={frozen}
        onCheckedChange={handleToggle}
        aria-label={`${tx.description} em ${shortDate(tx.date)}`}
      />
      <span className="shrink-0 font-mono text-[11px] tabular-nums text-text-tertiary">
        {shortDate(tx.date)}
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn('block truncate text-xs', checked ? 'text-text-primary' : 'text-text-tertiary')}>
          {tx.description}
        </span>
        <span className="block text-[11px] text-text-tertiary">{tx.accountName}</span>
      </span>
      <span
        className={cn(
          'shrink-0 font-mono text-xs tabular-nums',
          !checked ? 'text-text-tertiary' : tx.amount > 0 ? 'text-amount-positive' : 'text-amount-negative',
        )}
      >
        {withExplicitPlus(formatCurrency(tx.amount), tx.amount)}
      </span>
    </li>
  );
}

function StreamOptionButton({
  stream,
  cls,
  label,
  disabled,
  onPick,
}: {
  stream: StreamCandidate;
  cls: SemanticClassWire;
  label: string;
  disabled: boolean;
  onPick: (stream: StreamCandidate, cls: SemanticClassWire) => void;
}) {
  const handlePick = useCallback(() => onPick(stream, cls), [onPick, stream, cls]);
  return (
    <Button variant="outline" size="sm" disabled={disabled} onClick={handlePick}>
      {label}
    </Button>
  );
}

/**
 * The "É renda" perimeter follow-up (VER-363/365): when the payer is the user
 * themself, income implies an account outside the connected perimeter — record
 * WHICH account so pairing never mistakes it for an internal transfer.
 */
function IncomeFollowUp({
  stream,
  declaredAccounts,
  posting,
  onConfirmIncome,
  onCancel,
}: {
  stream: StreamCandidate;
  declaredAccounts: DeclaredExternalAccount[];
  posting: boolean;
  onConfirmIncome: StreamCardProps['onConfirmIncome'];
  onCancel: () => void;
}) {
  const [newName, setNewName] = useState('');

  const handleNameChange = useCallback((event: FormEvent<HTMLInputElement>) => {
    setNewName(event.currentTarget.value);
  }, []);

  const handleDeclareNew = useCallback(() => {
    const name = newName.trim();
    if (!name) return;
    onConfirmIncome(stream, { newAccountName: name });
  }, [newName, onConfirmIncome, stream]);

  const handleNotMine = useCallback(() => onConfirmIncome(stream, {}), [onConfirmIncome, stream]);

  return (
    <div className="mt-3 rounded-xl border border-border-brand bg-brand-50 p-3.5">
      <p className="text-[13px] font-semibold text-primary">
        Esse dinheiro vem de uma conta sua que não dá para conectar?
      </p>
      <p className="mt-1 text-xs/5 text-pretty text-primary/80">
        Se vier, a gente registra essa conta para nunca confundir a sua renda com uma
        transferência entre contas suas.
      </p>

      {declaredAccounts.length > 0 && (
        <ul className="mt-3 grid gap-1.5">
          {declaredAccounts.map((account) => (
            <DeclaredAccountRow
              key={account.id}
              stream={stream}
              account={account}
              posting={posting}
              onConfirmIncome={onConfirmIncome}
            />
          ))}
        </ul>
      )}

      <div className="mt-3 space-y-1.5">
        <Label htmlFor={`follow-up-name-${stream.streamKey}`} className="text-xs">
          Sim — de qual conta?
        </Label>
        <div className="flex gap-2">
          <Input
            id={`follow-up-name-${stream.streamKey}`}
            value={newName}
            onInput={handleNameChange}
            placeholder="Ex.: conta PJ da minha empresa"
            // 16px on phones — see the search field in TotalsCheckCard: below that,
            // iOS Safari zooms in on focus and does not zoom back out.
            className="h-8 bg-surface text-base md:text-xs"
          />
          <Button
            variant="outline"
            size="sm"
            disabled={posting || newName.trim().length === 0}
            onClick={handleDeclareNew}
          >
            Salvar
          </Button>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3.5">
        <Button variant="link" size="sm" disabled={posting} onClick={handleNotMine}>
          Não, vem de outra pessoa ou empresa
        </Button>
        <Button variant="link" size="sm" disabled={posting} onClick={onCancel}>
          Voltar
        </Button>
      </div>
    </div>
  );
}

function DeclaredAccountRow({
  stream,
  account,
  posting,
  onConfirmIncome,
}: {
  stream: StreamCandidate;
  account: DeclaredExternalAccount;
  posting: boolean;
  onConfirmIncome: StreamCardProps['onConfirmIncome'];
}) {
  const handlePick = useCallback(
    () => onConfirmIncome(stream, { declaredExternalAccountId: account.id }),
    [onConfirmIncome, stream, account.id],
  );
  return (
    <li className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        disabled={posting}
        onClick={handlePick}
        className="bg-surface"
      >
        {account.name}
      </Button>
    </li>
  );
}
